import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Assistant } from '@/lib/templates/types';

export interface UseAssistantReturn {
  assistants: Assistant[];
  selectedAssistant: Assistant | null;
  selectAssistant: (assistant: Assistant | null) => void;
  isLoading: boolean;
}

export function useAssistant(): UseAssistantReturn {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadAssistants();
  }, []);

  const loadAssistants = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('assistants')
        .select('*')
        .eq('active_status', true)
        .order('assistant_name');

      if (error) {
        console.warn('Failed to load assistants:', error.message);
        return;
      }

      if (data) {
        setAssistants(data as Assistant[]);
      }

      // Load surgeon default assistant
      try {
        const { data: authData } = await supabase.auth.getUser();
        if (authData?.user) {
          const { data: defaultMapping } = await supabase
            .from('surgeon_default_assistants')
            .select('assistant_id')
            .eq('surgeon_id', authData.user.id)
            .maybeSingle();

          if (defaultMapping && data) {
            const defaultAssistant = (data as Assistant[]).find(
              a => a.id === defaultMapping.assistant_id
            );
            if (defaultAssistant) {
              setSelectedAssistant(defaultAssistant);
            }
          }
        }
      } catch (authErr) {
        console.warn('Failed to load default assistant:', authErr);
      }
    } catch (err) {
      console.warn('useAssistant load error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const selectAssistant = useCallback((assistant: Assistant | null) => {
    setSelectedAssistant(assistant);
  }, []);

  return { assistants, selectedAssistant, selectAssistant, isLoading };
}
